import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@web/components/Button';
import { Drawer } from '@web/components/Drawer';
import { ErrorState, LoadingBar } from '@web/components/Feedback';
import { ReasonChip } from '@web/components/ReasonChip';
import { StateChip } from '@web/components/StateChip';
import { TemperatureMeter } from '@web/components/TemperatureMeter';
import { WindowCountdown } from '@web/components/Timers';
import { toast } from '@web/hooks/useToast';
import { useTakeOver } from '@web/features/thread/mutations';
import { Transcript } from '@web/features/thread/Transcript';
import { useConversation, useWatchThread } from '@web/features/thread/useThread';

/**
 * Peek at a conversation without joining it.
 *
 * Read-only on purpose: an operator looking at the board is triaging, and the
 * moment they can type here they have joined a conversation without the state
 * change that tells everyone else. The only way in is Take over, which is where
 * initial focus lands — a peek that ends in "yes, I'll take it" should be one
 * Enter, not a hunt for the button.
 */
export function PeekDrawer({
  conversationId,
  onClose,
}: {
  conversationId: string | null;
  onClose: () => void;
}) {
  const open = conversationId !== null;
  return open ? <PeekBody conversationId={conversationId} onClose={onClose} /> : null;
}

function PeekBody({ conversationId, onClose }: { conversationId: string; onClose: () => void }) {
  const navigate = useNavigate();
  const conversation = useConversation(conversationId);
  const takeOver = useTakeOver();
  const primary = useRef<HTMLButtonElement>(null);
  useWatchThread(conversationId);

  const detail = conversation.data;
  const claimedElsewhere = detail?.handlingState === 'human';
  const holder = detail?.assignee?.name ?? null;

  // If a colleague claims it while this panel is open, say so and get out of
  // the way. UI that silently changes meaning under a reader is worse than UI
  // that closes.
  useEffect(() => {
    if (!claimedElsewhere) return;
    toast('info', `${holder ?? 'A colleague'} took this conversation`);
  }, [claimedElsewhere, holder]);

  return (
    <Drawer
      open
      onClose={onClose}
      title={
        detail?.contact.name ?? detail?.contact.pushName ?? detail?.contact.phone ?? 'Conversation'
      }
      initialFocusRef={primary}
      subtitle={
        detail === undefined ? null : (
          <div className="flex flex-wrap items-center gap-2">
            <StateChip state={detail.handlingState} />
            <TemperatureMeter temperature={detail.temperature} />
            <span className="font-mono text-[11px] text-ink-faint tabular-nums">
              {detail.contact.phone}
            </span>
            <span className="text-[11px] text-ink-faint">{detail.trade ?? 'trade unknown'}</span>
            <WindowCountdown lastInboundAt={detail.lastInboundAt} size="full" />
            {detail.handlingState === 'parked' && <ReasonChip reason={detail.parkReason} />}
          </div>
        )
      }
      footer={
        <div className="flex items-center justify-between gap-3">
          <p className="text-[11px] text-ink-faint">Read-only. Take over to reply.</p>
          <div className="flex gap-2">
            <Button
              size="touch"
              onClick={() => {
                void navigate(`/thread/${conversationId}`);
              }}
            >
              Open thread
            </Button>
            <Button
              ref={primary}
              tone="primary"
              size="touch"
              disabled={takeOver.isPending}
              onClick={() => {
                takeOver.mutate(
                  { conversationId, body: { reason: 'manual' } },
                  {
                    onSuccess: () => {
                      void navigate(`/thread/${conversationId}`);
                    },
                  },
                );
              }}
            >
              {takeOver.isPending ? 'Taking over…' : 'Take over'}
            </Button>
          </div>
        </div>
      }
    >
      {conversation.isPending ? (
        <LoadingBar label="Loading conversation" />
      ) : conversation.error !== null ? (
        <ErrorState error={conversation.error} />
      ) : (
        <Transcript conversationId={conversationId} />
      )}
    </Drawer>
  );
}
